import React, { useCallback } from 'react';
import { Trans } from '@lingui/macro';
import {
    Heading,
    Group,
    ArrowForwardOutlined,
    CaretRightOutlined,
    Button,
    Box,
    IconProps,
    MaestroComponent,
    spacing,
    radius,
    color,
    useBreakpoint,
    palette,
} from '@landr/maestro';
import { Link, useNavigate } from 'react-router-dom';
import { RoutePaths } from 'types/enums';
import styled from 'styled-components';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { noop } from 'utils';
import { TrendingLink } from '../type';
import { TrendingLinks } from './TrendingLinks';

const TrendingCardWrapper = styled(Box)`
    border: 1px solid ${color('neutral.900')};
    border-radius: ${radius('base')};

    width: calc((100% - ${spacing('xl')} * 3) / 4);

    &:hover {
        cursor: pointer;
        background-color: ${palette('neutral.900')};

        background: radial-gradient(
            100% 50% at 100% 0.1%,
            rgb(44, 60, 77) 0%,
            rgb(20, 39, 57) 100%
        );
    }

    ${({ theme }): string => theme.breakpoint('lg')`
        width: calc((100% - ${spacing('xl')}) / 2);
    `}

    ${({ theme }): string => theme.breakpoint('ms')`
        width: 100%;
    `}
`;

const TrendingCardContainer = styled(Box)`
    padding: ${spacing('xxl')};

    ${({ theme }): string => theme.breakpoint('ms')`
        padding: ${spacing('lg')};
    `}
`;

const MobileTrendingCardGroup = styled(Group)`
    ${({ theme }): string => theme.breakpoint('ms')`
        justify-content: space-between;
        flex-wrap: nowrap;
        align-content: center;

        > * {
            display: inline-flex;
        }
    `}
`;

const ThinnerSvg = styled(Box)`
    align-items: center;
    display: flex;

    svg g,
    svg path {
        stroke-width: 0.75;
    }
`;

const LinkWrapper = styled(Link)`
    display: block;
    width: 100%;
    height: 100%;
`;

export const TrendingCard = ({
    icon,
    title,
    path,
    trendings,
    onClick = noop,
}: {
    icon: MaestroComponent<IconProps, Record<string, any>>;
    title: string;
    trendings: TrendingLink[];
    path: RoutePaths;
    onClick?: () => void;
}) => {
    const toLocalizedPath = useToLocalizedPath();
    const localizedPath = toLocalizedPath(`/${path}`);
    const navigate = useNavigate();

    const isMs = useBreakpoint('ms');

    const Icon = icon;

    const handleClickWrapper = useCallback(() => {
        onClick();
        navigate({
            pathname: localizedPath,
        });
    }, [onClick, localizedPath, navigate]);

    const handleClickOnAnchors = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            onClick();
        },
        [onClick],
    );

    if (isMs) {
        return (
            <TrendingCardWrapper>
                <LinkWrapper to={localizedPath} onClick={onClick}>
                    <TrendingCardContainer>
                        <MobileTrendingCardGroup
                            direction="horizontal"
                            gutter="xs"
                            alignItems="center"
                        >
                            <Group.Item>
                                <ThinnerSvg>
                                    <Icon size={34} color="primary.400" />
                                </ThinnerSvg>
                            </Group.Item>
                            <Group.Item flexGrow={10}>
                                <Heading as="h3" size="base" mb={0}>
                                    {title}
                                </Heading>
                            </Group.Item>
                            <Group.Item>
                                <CaretRightOutlined />
                            </Group.Item>
                        </MobileTrendingCardGroup>
                    </TrendingCardContainer>
                </LinkWrapper>
            </TrendingCardWrapper>
        );
    }

    return (
        <TrendingCardWrapper onClick={handleClickWrapper}>
            <TrendingCardContainer>
                <Group direction="vertical" gutter="xl">
                    <Group.Item>
                        <ThinnerSvg>
                            <Icon size={44} color="primary.400" />
                        </ThinnerSvg>
                    </Group.Item>
                    <Group.Item>
                        <Group direction="vertical" gutter="xs">
                            <Group.Item>
                                <Heading as="h3" size="xl">
                                    <Link
                                        to={localizedPath}
                                        onClick={handleClickOnAnchors}
                                    >
                                        {title}
                                    </Link>
                                </Heading>
                            </Group.Item>
                            <Group.Item>
                                <TrendingLinks
                                    trendings={trendings}
                                    onClick={handleClickOnAnchors}
                                />
                            </Group.Item>
                        </Group>
                    </Group.Item>
                    <Group.Item>
                        <Button
                            iconRight={ArrowForwardOutlined}
                            kind="link"
                            variant="secondary"
                            as={Link}
                            to={localizedPath}
                            onClick={handleClickOnAnchors}
                        >
                            <Trans>See all</Trans>
                        </Button>
                    </Group.Item>
                </Group>
            </TrendingCardContainer>
        </TrendingCardWrapper>
    );
};
